// A manager's Drivers on the till (T34, UC-65, #1003): this shop's list of
// drivers, the one the owner keeps on the dashboard (D28), read and changed
// through /api/till/drivers, which the till's order store forwards to the
// server signed. Every rule is the server's: a name of one line, 1 to
// MaxDriverNameLength characters once trimmed, unique in the shop with the
// removed drivers, and only a manager changes it. The screen says each
// refusal in plain words and keeps nothing in the browser.
//
// A store that keeps the list answers its read offline from what it kept;
// the screen then shows it read-only, since every change needs the server.
// An older store cannot answer the read offline, and the screen says the
// list could not be loaded, with Retry.
//
// A removal is undone, not confirmed: the driver leaves the list at once and
// the server hears of it only when the 10-second Undo runs out, or at once
// when another removal, a lock or a sign-out comes first.

import { api } from '../api/client';
import type { components } from '../api/types';
import type { MessageKey } from '../i18n/messages';
import { problemOf } from './answer';

export type Driver = components['schemas']['DriverBody'];

/** How long a read or a change waits before the screen says it is waiting. */
export const DRIVERS_SLOW_MS = 300;

/**
 * How long a lock waits for a removal it sends before it locks the store
 * too, so a slow link never leaves the store's session open for long.
 */
export const REMOVAL_BEFORE_LOCK_MS = 2000;

/** What became of a change, in the words the screen says it in. */
export type DriverChange =
	'saved' | 'taken' | 'bounds' | 'gone' | 'internet' | 'forbidden' | 'failed';

/**
 * A change's answer as the screen says it: a name another driver of the shop
 * holds or held, a name out of bounds, a driver someone else removed (a 404
 * at the driver), no answer from Bosla (a 502, 503 or
 * 504 that names no code, such as the store's empty 502), a person who is no
 * manager, or anything else, which did not go through.
 */
export function driverChange(status: number | null, code?: string): DriverChange {
	if (status !== null && status >= 200 && status < 300) return 'saved';
	if (status === 409 && code === 'DRIVER_NAME_TAKEN') return 'taken';
	if (status === 422 && code === 'VALIDATION_ERROR') return 'bounds';
	if (status === 404 && code === 'NOT_FOUND') return 'gone';
	if (status === 403 && code === 'FORBIDDEN') return 'forbidden';
	if (status === null || ((status === 502 || status === 503 || status === 504) && !code))
		return 'internet';
	return 'failed';
}

/** The words of a change that did not go through; none for a saved one. */
export function driverChangeWords(change: DriverChange): MessageKey | null {
	const keys: Record<DriverChange, MessageKey | null> = {
		saved: null,
		taken: 'tillDriversNameTaken',
		bounds: 'tillDriversNameBounds',
		gone: 'tillDriversGone',
		internet: 'tillDriversNeedsInternet',
		forbidden: 'tillDriversForbidden',
		failed: 'printerFailed',
	};
	return keys[change];
}

/**
 * Whether a failed read is worth a Retry: no answer, a 5xx, or a 404 that
 * names no field, which is a server without the drivers' routes, as in a
 * deploy's swap (.claude/rules/web.md, Retry only for network and 5xx).
 */
export function readRetryable(status: number | null, error: unknown): boolean {
	if (status === null || status >= 500) return true;
	return status === 404 && !problemOf(error)?.details?.path;
}

/** The Drivers row of Till settings: how many drivers, once a read answered. */
export function driversRowState(count: number | null): { key: MessageKey; count: number } | null {
	if (count === null) return null;
	if (count === 0) return { key: 'tillDriversNone', count };
	return { key: count === 1 ? 'tillDriversOne' : 'tillDriversMany', count };
}

/** This shop's drivers as the till last read them, and a manager's changes. */
export class TillDrivers {
	/** The shop's active drivers in name order, as the server sends them. */
	list = $state<Driver[]>([]);
	/** A read answered with the list; false before one, and after a failed one. */
	readOk = $state(false);
	/** A read is on its way, and past DRIVERS_SLOW_MS once `slow`. */
	loading = $state(false);
	slow = $state(false);
	/** The last read got no answer or a refusal other than FORBIDDEN. */
	failed = $state(false);
	/** Retry is offered for the failure (readRetryable). */
	retryable = $state(false);
	/** A read or a change was refused FORBIDDEN: the one signed in is no manager. */
	forbidden = $state(false);
	/** A change is on its way, and past DRIVERS_SLOW_MS once `savingSlow`. */
	saving = $state(false);
	savingSlow = $state(false);
	/** A removal's own refusal, said under the list; null while there is none. */
	failure = $state<MessageKey | null>(null);
	/**
	 * The name being typed, of a driver or of a new one (`id` "new"), with
	 * the name it started from, kept here so a lock or the Sheet's close
	 * keeps it; null while none is.
	 */
	edit = $state<{ id: string; from: string; name: string; error: MessageKey | null } | null>(null);
	/** A removal waiting out its Undo; null while none is. */
	removal = $state<Driver | null>(null);
	/** The drivers whose removal is on its way to the server. */
	sending = $state<string[]>([]);

	// Reads can overlap, a change's and the sheet's: only the latest asked
	// replaces the list.
	#asked = 0;

	/** The removal the Undo shows for, if any. */
	get pendingRemoval(): Driver | null {
		return this.removal;
	}

	/** A removal waits out its Undo or is on its way: a reload would lose it (INT-7). */
	get unsettled(): boolean {
		return this.removal !== null || this.sending.length > 0;
	}

	/** A name was typed that a reload would lose: an edit that changed its name. */
	get typing(): boolean {
		return this.edit !== null && this.edit.name.trim() !== this.edit.from.trim();
	}

	/** The list the screen shows: the read's, less every removal not yet answered. */
	get shown(): Driver[] {
		const hidden = [...this.sending, ...(this.removal ? [this.removal.id] : [])];
		return hidden.length ? this.list.filter((d) => !hidden.includes(d.id)) : this.list;
	}

	/** Another person signed in: forget what the last one saw. */
	reset(): void {
		this.#asked++;
		this.list = [];
		this.readOk = false;
		this.loading = false;
		this.slow = false;
		this.failed = false;
		this.retryable = false;
		this.forbidden = false;
		this.failure = null;
		this.edit = null;
	}

	/** Read the list again. */
	async read(): Promise<void> {
		const mine = ++this.#asked;
		this.loading = true;
		this.slow = false;
		const timer = setTimeout(() => {
			if (this.#asked === mine) this.slow = true;
		}, DRIVERS_SLOW_MS);
		try {
			const { data, error, response } = await api.GET('/api/till/drivers');
			if (mine !== this.#asked) return;
			if (data && error === undefined) {
				this.list = data.drivers ?? [];
				// A rename of a driver someone else removed has nothing left to name.
				const editing = this.edit?.id;
				if (editing && editing !== 'new' && !this.list.some((d) => d.id === editing))
					this.edit = null;
				this.readOk = true;
				this.failed = false;
				this.retryable = false;
				this.forbidden = false;
				return;
			}
			this.forbidden = response.status === 403 && problemOf(error)?.code === 'FORBIDDEN';
			this.failed = !this.forbidden;
			this.retryable = readRetryable(response.status, error);
			this.readOk = false;
		} catch {
			// The store did not answer.
			if (mine !== this.#asked) return;
			this.failed = true;
			this.retryable = true;
			this.readOk = false;
		} finally {
			clearTimeout(timer);
			if (mine === this.#asked) {
				this.loading = false;
				this.slow = false;
			}
		}
	}

	/** Start typing a name: a driver's, or a new one's with `driver` null. */
	startEdit(driver: Driver | null): void {
		this.failure = null;
		this.edit = driver
			? { id: driver.id, from: driver.name, name: driver.name, error: null }
			: { id: 'new', from: '', name: '', error: null };
	}

	/**
	 * Add a driver (`id` null) or rename one. It counts as saving until the
	 * list read after it answered, so the saved name is never sent twice.
	 */
	async save(id: string | null, name: string): Promise<DriverChange> {
		if (this.saving) return 'failed';
		this.saving = true;
		this.savingSlow = false;
		this.failure = null;
		const timer = setTimeout(() => (this.savingSlow = this.saving), DRIVERS_SLOW_MS);
		try {
			const body = { name: name.trim() };
			let change = await this.#send(() =>
				id === null
					? api.POST('/api/till/drivers', { body })
					: api.PATCH('/api/till/drivers/{driverId}', {
							params: { path: { driverId: id } },
							body,
						}),
			);
			// Only a driver's own route answers that he is gone; an add's 404 is a
			// server without the drivers' routes, as in a deploy's swap.
			if (change === 'gone' && id === null) change = 'failed';
			// A driver someone else removed leaves the list once it is read again,
			// and that read ends his edit (read, above); a failed read keeps it.
			if (change === 'saved' || change === 'gone') await this.read();
			return change;
		} finally {
			clearTimeout(timer);
			this.saving = false;
			this.savingSlow = false;
		}
	}

	/** Remove now, undoably. A removal still waiting is sent first. */
	remove(driver: Driver): void {
		void this.commitRemoval();
		this.failure = null;
		this.removal = driver;
	}

	/** Undo: the driver comes back and the server never hears of it. */
	undoRemoval(): void {
		this.removal = null;
	}

	/**
	 * The Undo ran out, or something came first: tell the server. The driver
	 * stays hidden while it is on its way; a refusal brings him back with
	 * its words. The list is read again behind it, so nothing waits for it.
	 */
	async commitRemoval(): Promise<void> {
		const driver = this.removal;
		if (!driver) return;
		this.removal = null;
		this.sending = [...this.sending, driver.id];
		const change = await this.#send(() =>
			api.POST('/api/till/drivers/{driverId}/remove', {
				params: { path: { driverId: driver.id } },
			}),
		);
		if (change === 'saved') this.list = this.list.filter((d) => d.id !== driver.id);
		else this.failure = driverChangeWords(change);
		this.sending = this.sending.filter((id) => id !== driver.id);
		void this.read();
	}

	/**
	 * Before a lock: send a removal still waiting for its Undo, and wait for
	 * its answer at most REMOVAL_BEFORE_LOCK_MS.
	 */
	async settleBeforeLock(): Promise<void> {
		if (!this.removal) return;
		let timer: ReturnType<typeof setTimeout> | undefined;
		await Promise.race([
			this.commitRemoval(),
			new Promise<void>((done) => (timer = setTimeout(done, REMOVAL_BEFORE_LOCK_MS))),
		]);
		clearTimeout(timer);
	}

	async #send(call: () => Promise<{ error?: unknown; response: Response }>): Promise<DriverChange> {
		try {
			const { error, response } = await call();
			const change = driverChange(response.status, problemOf(error)?.code);
			if (change === 'forbidden') this.forbidden = true;
			return change;
		} catch {
			return 'internet';
		}
	}
}
